import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { TodoList } from '../../../../requests/todos';

const GET_TODO_LIST = gql`
  query TodoList {
    todoList @rest(type: "TodoList", path: "/todo/list") {
      _id
      name
      list
      default
      order
      googleCalendars
      participants
      tags {
        _id
        name
        color
      }
      color
      icon
      boardId
      tableId
    }
  }
`;

export const useTodoListQuery = () => {
  const { data, loading, error, refetch } = useQuery<{ todoList: TodoList[] }>(
    GET_TODO_LIST,
  );

  const [defaultTodoList, defaultTodoListId] = useMemo(() => {
    const defaultList =
      data?.todoList?.find((_) => _.default) || data?.todoList[0];
    return [defaultList, defaultList?._id || ''];
  }, [data]);

  const todoList = useMemo(() => {
    const [assignedToMe, ...list] =
      data?.todoList
        .filter((_) => !_.list && _._id !== defaultTodoListId)
        .sort((a, b) => a.order - b.order)
        .map((_) => ({
          ..._,
          subLists: data?.todoList
            .filter((__) => __.list === _._id)
            .sort((a, b) => a.order - b.order),
        })) || [];
    return assignedToMe ? [...(list || []), assignedToMe] : [];
  }, [data, defaultTodoListId]);

  const ordersList = useMemo(
    () => todoList.map((_) => _?.order).filter((_) => _),
    [todoList],
  );

  const ordersSubList = useMemo(
    () =>
      todoList.reduce(
        (acc, curr) => {
          acc[curr._id] = curr.subLists.map((_) => _.order);
          return acc;
        },
        {} as Record<string, number[]>,
      ),
    [todoList],
  );

  return {
    todoList,
    defaultTodoList,
    defaultTodoListId,
    isTodosLoading: loading,
    isTodosError: error,
    ordersList,
    ordersSubList,
    refetchTodoList: refetch,
  };
};
